// The 12 approved colour themes of the personal welcome screen (2026-10-16). Only the welcome
// screen uses them. `bg` and `accent` are the approved values; every other colour is derived so
// text and controls stay readable (WCAG contrast) on each background:
//   accentText - the accent, darkened only as much as needed for >= 4.5:1 on the background
//                (the inspiration line and the send button use it)
//   text/muted - dark tinted neutrals for the greeting and secondary text
//   border/glow - soft tints of the accent

export const THEMES = [
  { id: 'pearl', he: 'לבן פנינה', en: 'Pearl white', bg: '#FFFFFF', accent: '#6756DB' },
  { id: 'sky', he: 'תכלת שמיים', en: 'Sky blue', bg: '#E8F5FF', accent: '#2584D8' },
  { id: 'lavender', he: 'לבנדר', en: 'Lavender', bg: '#F0EAFF', accent: '#8262D8' },
  { id: 'mint', he: 'מנטה', en: 'Mint', bg: '#E4F8ED', accent: '#269A70' },
  { id: 'powder', he: 'ורוד פודרה', en: 'Powder pink', bg: '#FFF0F4', accent: '#D75C91' },
  { id: 'peach', he: 'אפרסק', en: 'Peach', bg: '#FFF0E5', accent: '#E78650' },
  { id: 'vanilla', he: 'צהוב וניל', en: 'Vanilla', bg: '#FFF9D9', accent: '#A77D17' },
  { id: 'ice', he: 'כחול קרח', en: 'Ice blue', bg: '#EDF5FA', accent: '#548EBD' },
  { id: 'lilac', he: 'לילך', en: 'Lilac', bg: '#F8EAFE', accent: '#AD65C8' },
  { id: 'sage', he: 'ירוק מרווה', en: 'Sage green', bg: '#EEF4E7', accent: '#6B9761' },
  { id: 'coral', he: 'קורל', en: 'Coral', bg: '#FFEAE6', accent: '#D76D61' },
  { id: 'silk', he: 'אפור משי', en: 'Silk grey', bg: '#F2F4F7', accent: '#637C9D' }
];

export const THEME_COUNT = 12;

function hexToRgb(hex) {
  const value = String(hex).replace('#', '');
  return [0, 2, 4].map(offset => parseInt(value.slice(offset, offset + 2), 16));
}

function rgbToHex(rgb) {
  return `#${rgb.map(channel => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}

export function luminance(hex) {
  const [r, g, b] = hexToRgb(hex).map(channel => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a, b) {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (light + 0.05) / (dark + 0.05);
}

/** Mixes a colour toward another colour (0 = unchanged, 1 = target). */
export function mix(hex, targetHex, amount) {
  const from = hexToRgb(hex);
  const to = hexToRgb(targetHex);
  return rgbToHex(from.map((channel, index) => channel + (to[index] - channel) * amount));
}

/** The accent darkened just enough to reach `minimum` contrast on the background. */
export function readableAccent(accent, bg, minimum = 4.5) {
  let color = accent;
  for (let step = 0; step <= 20 && contrastRatio(color, bg) < minimum; step += 1) color = mix(accent, '#000000', step * 0.05);
  return color;
}

/** Full palette for one theme index (safe for any input - falls back to the first theme). */
export function themePalette(index) {
  const theme = THEMES[Number.isInteger(index) && index >= 0 && index < THEMES.length ? index : 0];
  const accentText = readableAccent(theme.accent, theme.bg, 4.5);
  return {
    ...theme,
    accentText,
    text: mix(theme.accent, '#0B1220', 0.82),
    muted: mix(theme.accent, '#334155', 0.7),
    border: mix(theme.accent, '#FFFFFF', 0.72),
    glow: mix(theme.accent, theme.bg, 0.82),
    inputBg: '#FFFFFF'
  };
}

/** CSS custom properties for the welcome screen. */
export function themeStyle(index) {
  const p = themePalette(index);
  return {
    '--welcome-bg': p.bg,
    '--welcome-accent': p.accent,
    '--welcome-accent-text': p.accentText,
    '--welcome-text': p.text,
    '--welcome-muted': p.muted,
    '--welcome-border': p.border,
    '--welcome-glow': p.glow,
    '--welcome-input-bg': p.inputBg
  };
}
